import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useJsApiLoader, GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';

// static urls for marker icons
const USER_ICON_URL  = process.env.PUBLIC_URL + '/images/user-icon.svg';
const PHARM_ICON_URL = process.env.PUBLIC_URL + '/images/pharm-icon.svg';

// container and default options
const containerStyle = { width: '100%', height: '100%' };
const DEFAULT_ZOOM    = 13;
const libraries       = ['places'];

export default function MapView({ userLocation, pharmacies, searchTerm, radius }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const [map, setMap] = useState(null);
  const markersRef = useRef([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);

  // onLoad callback for GoogleMap
  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapInstance.panTo(userLocation);
    mapInstance.setZoom(DEFAULT_ZOOM);
  }, [userLocation]);

  useEffect(() => {
    if (!isLoaded || !map) return;

    // 1) Clear existing markers
    markersRef.current.forEach(({ marker }) => {
      try { marker.setMap(null); }
      catch (err) { console.error('Error clearing marker:', err); }
    });
    markersRef.current = [];
    setSelectedPharmacy(null);

    if (!pharmacies.length) return;

    // 2) Limit to 30 closest
    const topList = [...pharmacies]
      .sort((a, b) => a.distance_km - b.distance_km)
      .slice(0, 30);

    // 3) Place markers using stored DB coordinates — the same coords the
    //    radius filter ran against. No re-geocoding here.
    topList.forEach((pharm) => {
      const position = {
        lat: parseFloat(pharm.lat),
        lng: parseFloat(pharm.lng)
      };

      if (isNaN(position.lat) || isNaN(position.lng)) {
        console.warn('Skipping pharmacy with invalid coords:', pharm.name);
        return;
      }

      const marker = new window.google.maps.Marker({
        map,
        position,
        icon: {
          url: PHARM_ICON_URL,
          scaledSize: new window.google.maps.Size(36, 36)
        }
      });

      // On click: fetch formatted address from Places API on demand,
      // fall back to the DB address if the lookup fails
      marker.addListener('click', () => {
        const service = new window.google.maps.places.PlacesService(map);
        service.findPlaceFromQuery(
          {
            query: `${pharm.name} ${pharm.address}`,
            fields: ['formatted_address']
          },
          (results, status) => {
            const formattedAddress =
              status === window.google.maps.places.PlacesServiceStatus.OK && results?.[0]
                ? results[0].formatted_address
                : pharm.address;
            setSelectedPharmacy({ pharmacy: pharm, position, formattedAddress });
          }
        );
      });

      markersRef.current.push({ marker, pharm });
    });

    // 4) Fit bounds to user location + ALL markers
    if (markersRef.current.length) {
      try {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(userLocation);
        markersRef.current.forEach(({ marker }) => bounds.extend(marker.getPosition()));
        map.fitBounds(bounds, 50);
      } catch (bErr) {
        console.error('Error fitting bounds:', bErr);
      }
    }
  }, [isLoaded, map, pharmacies, userLocation]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading map…</div>;

  return (
    <>
    {pharmacies.length === 0 && searchTerm && (
      <div style={{
        position: 'absolute',
        bottom: '2rem',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(255,255,255,0.92)',
        padding: '0.75rem 1.25rem',
        borderRadius: '8px',
        fontSize: '0.9rem',
        color: '#555',
        zIndex: 10,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}>
        No pharmacies found stocking &ldquo;{searchTerm}&rdquo; within {radius} km.
      </div>
    )}
    <GoogleMap
      mapContainerStyle={containerStyle}
      onLoad={onLoad}
      options={{ disableDefaultUI: true, zoomControl: true }}
    >
      {/* User location */}
      <Marker
        position={userLocation}
        icon={{
          url: USER_ICON_URL,
          scaledSize: new window.google.maps.Size(48, 48)
        }}
      />

      {/* Controlled InfoWindow */}
      {selectedPharmacy && (
        <InfoWindow
          position={selectedPharmacy.position}
          onCloseClick={() => setSelectedPharmacy(null)}
        >
          <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '240px' }}>
            <h3 style={{
              margin: '0 0 0.5rem',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}>{selectedPharmacy.pharmacy.name}</h3>
            <p style={{ margin: '0.25rem 0' }}>
              {selectedPharmacy.formattedAddress}
            </p>
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Drug:</strong>{' '}
              {(() => {
                const raw = selectedPharmacy.pharmacy.matched_drugs[0] || '';
                return raw.charAt(0).toUpperCase() + raw.slice(1);
              })()}
            </p>
            <p style={{ margin: '0.25rem 0' }}>
              <strong>Distance:</strong> {parseFloat(selectedPharmacy.pharmacy.distance_km).toFixed(2)} km
            </p>
            <a
              href={`https://www.google.com/maps/dir/?api=1&origin=${
                userLocation.lat},${userLocation.lng
              }&destination=${
                selectedPharmacy.position.lat},${selectedPharmacy.position.lng
              }`}
              target="_blank" rel="noopener noreferrer"
              style={{
                color: '#4285f4',
                textDecoration: 'none',
                fontWeight: 'bold'
              }}
            >Get Directions</a>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
    </>
  );
}
